"use client";

import { retrieveCart } from "@/modules/cart/actions/cart"; // Server Action
import { useCurrencyStore } from "@/store/index";
import { HttpTypes } from "@medusajs/types";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, useState } from "react";

type CartContextType = {
  cart: HttpTypes.StoreCart | null;
  setCart: React.Dispatch<React.SetStateAction<HttpTypes.StoreCart | null>>;
  refreshCart: () => Promise<void>;
  clearCart: () => void;
  isPending: boolean;
};

const CartContext = createContext<CartContextType | null>(null);

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
  const [cart, setCart] = useState<HttpTypes.StoreCart | null>(null);
  const { currencyCode = 'EUR' } = useCurrencyStore();
  const queryClient = useQueryClient();

  const refreshCart = async () => {
    queryClient.invalidateQueries({ queryKey: ["cart", currencyCode] });
  };

  const clearCart = () => {
    setCart(null);
    queryClient.removeQueries({ queryKey: ["cart", currencyCode] });
  };

  const { isPending } = useQuery<HttpTypes.StoreCart | null>({
    queryKey: ["cart", currencyCode],
    queryFn: async () => {
      const res = await retrieveCart({
        currency_code: currencyCode.toLowerCase(),
      });
      setCart(res);
      return res;
    },
  });

  return (
    <CartContext.Provider value={{ cart, setCart, refreshCart, clearCart, isPending }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) throw new Error("useCart must be used within a CartProvider");
  return context;
};
